import { useExperimentStore, useLanguageStore, useRoutingStore, useUserStore } from '@/helpers/composables/useStores';
import { computed, ComputedRef, inject, ref } from 'vue';
import { injectLocal, MaybeRefOrGetter, provideLocal, toValue, useMounted } from '@vueuse/core';
import { ExperimentTrackEventOptions } from '@/components/experiments/Core/types';
import SingleQtvExp, { SingleQtvExpName, SingleQtvExpVariants } from '..';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { BuyboxParam, OfferLocation } from '@/helpers/offer-helper';
import { BuyBoxFilterOptions } from '@/stores/user.store';
import { getClickoutTrackingPlacement, OFFER_ENTITYID_KEY } from '@/helpers/composables/useProvideClickoutPlacement';
import { Offer } from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { ClickoutOfferDetails } from '@/interfaces/snowplow/clickout-context';
const experimentSymbol = Symbol('SingleQTVExp');

export type TrackSingleQtvExp = ReturnType<typeof useSingleQtvExp>['trackSingleQtvExp'];

const tagsByVariant = {
	[SingleQtvExpVariants.CONTROL]: 'amazon-sqtv-co-20',
	[SingleQtvExpVariants.VARIANT_1]: 'amazon-sqtv-{replace}-v1-20',
	[SingleQtvExpVariants.VARIANT_2]: 'amazon-sqtv-v2-20',
	[SingleQtvExpVariants.VARIANT_3]: 'amazon-sqtv-v3-20',
	[SingleQtvExpVariants.VARIANT_4]: 'amazon-sqtv-v4-20',
	[SingleQtvExpVariants.VARIANT_5]: 'amazon-sqtv-v5-20',
};

export const useSingleQtvExp = (isExpActiveCondition?: ComputedRef<boolean>) => {
	const { activeVariantsWithControlGroup } = useExperimentStore();
	const { isLoggedIn } = useUserStore();
	const isMounted = useMounted();
	const { country } = useLanguageStore();
	const { activeRoute } = useRoutingStore();

	if (isExpActiveCondition) {
		provideLocal(experimentSymbol, isExpActiveCondition);
	}

	const isExpActive = injectLocal<ComputedRef<boolean>>(
		experimentSymbol,
		computed(() => false)
	);

	const expActiveVariant = computed(() => {
		if (
			!isMounted.value ||
			isLoggedIn.value ||
			!isExpActive.value ||
			!activeRoute.value.name.includes('app.detail')
		) {
			return undefined;
		}
		return activeVariantsWithControlGroup.value[SingleQtvExpName] as SingleQtvExpVariants | undefined;
	});

	function trackSingleQtvExp(TrackingOptions: ExperimentTrackEventOptions<SingleQtvExpVariants>) {
		if (expActiveVariant.value) {
			SingleQtvExp.trackEvent({
				property: 'single-qtv',
				...TrackingOptions,
			});
		}
	}

	const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
	const filterOption = inject('filterOption', ref<BuyBoxFilterOptions | undefined>(undefined));

	const offerLocationInjection = inject<OfferLocation>('offerLocation', OfferLocation.REGULAR_BUYBOX);
	const { clickoutType, placement } = getClickoutTrackingPlacement('qtv');
	const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);

	function getClickoutUrl(
		_offer: MaybeRefOrGetter<Offer | (Offer & ClickoutOfferDetails)>,
		title: TitleDetail,
		replacement: string = ''
	) {
		const offer = toValue(_offer);
		const offerEntityId = episodeEntityId ?? title.id;
		const uct_ct = expActiveVariant.value
			? tagsByVariant[expActiveVariant.value as SingleQtvExpVariants]?.replace('{replace}', replacement)
			: undefined;
		const clickoutUrl = new ClickoutUrl(offer, { skipYieldOptimization: true })
			.addContexts(titleContext.value)
			.setForTracking('offerLocation', offerLocationInjection)
			.updateContext('ClickoutContextGraphql', 'filterOption', filterOption.value)
			.updateContext('ClickoutContextGraphql', 'placement', placement)
			.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType)
			.setTitleSourceId(title.id)
			.set('uct_country', country.value)
			.set('uct_buybox', BuyboxParam.Normal)
			.updateContext('TitleContextGraphql', 'offerEntityId', offerEntityId);

		if (uct_ct) {
			const url = new URL(clickoutUrl.get('r')!);
			clickoutUrl.set('uct_ct', uct_ct);
			url.searchParams.set('tag', uct_ct);
			clickoutUrl.set('r', url.toString()!.replace('app.primevideo.com', 'www.primevideo.com'));
		}

		return clickoutUrl;
	}

	return {
		trackSingleQtvExp,
		expActiveVariant,
		getClickoutUrl,
	};
};
